import { useState, useEffect } from "react";
import { Package, Loader2, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Order {
  id: string;
  user_id: string;
  product_id: string;
  variant_id: string;
  quantity: number;
  total_price: number;
  status: string;
  created_at: string;
  product?: {
    name: string;
    cover_image: string;
  };
  profile?: {
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    email: string | null;
  };
}

const statusOptions = [
  { value: "pending", label: "Da prendere in ordinazione" },
  { value: "confirmed", label: "Preso in ordinazione" },
  { value: "preparing", label: "Preparazione" },
  { value: "packaging", label: "Imballaggio" },
  { value: "ready_pickup", label: "Pronto da ritirare" },
  { value: "ready_delivery", label: "Pronto per la consegna" },
  { value: "picked_up", label: "Ritirato" },
  { value: "delivered", label: "Consegnato" },
];

export const AdminOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          user_id,
          product_id,
          variant_id,
          quantity,
          total_price,
          status,
          created_at,
          product:products(name, cover_image)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch profiles for all orders (includes phone_number and email)
      const userIds = [...new Set((data || []).map(o => o.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, phone_number, email')
        .in('user_id', userIds);
      
      // Map profiles to orders
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);
      
      const ordersWithProfiles = (data || []).map(order => ({
        ...order,
        profile: profileMap.get(order.user_id) || null
      }));
      
      setOrders(ordersWithProfiles);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    setUpdatingId(orderId);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
      
      toast.success('Stato ordine aggiornato');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error("Errore nell'aggiornamento");
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mask name for privacy - show first name + initial of last name
  const getDisplayName = (profile: Order['profile']) => {
    if (!profile) return 'Cliente';
    const firstName = profile.first_name || '';
    const lastInitial = profile.last_name ? profile.last_name.charAt(0).toUpperCase() + '.' : '';
    return firstName ? `${firstName} ${lastInitial}`.trim() : 'Cliente';
  };

  // Format phone number for display (partial mask)
  const getDisplayPhone = (phone: string | null | undefined) => {
    if (!phone) return null;
    // Show phone without mask for admin
    return phone;
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchQuery.toLowerCase();
    const displayName = getDisplayName(order.profile).toLowerCase();
    return (
      displayName.includes(searchLower) ||
      order.product?.name?.toLowerCase().includes(searchLower) ||
      order.id.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-border/50">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca ordine..."
            className="pl-10"
          />
        </div>
      </div>

      {/* Orders List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">
              {searchQuery ? "Nessun ordine trovato" : "Nessun ordine"}
            </p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div
              key={order.id}
              className="p-4 rounded-xl bg-card/80 border border-border/50 space-y-3"
            >
              <div className="flex items-start gap-3">
                {order.product?.cover_image && (
                  <img 
                    src={order.product.cover_image} 
                    alt={order.product.name}
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {order.product?.name || 'Prodotto'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    👤 {getDisplayName(order.profile)}
                  </p>
                  {/* Email display for admin */}
                  {order.profile?.email && (
                    <p className="text-sm text-blue-500 dark:text-blue-400 flex items-center gap-1">
                      ✉️ {order.profile.email}
                    </p>
                  )}
                  {/* Phone number display for admin */}
                  {order.profile?.phone_number && (
                    <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                      📞 {getDisplayPhone(order.profile.phone_number)}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    🛒 Qta: {order.quantity} • €{order.total_price.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    📅 {formatDate(order.created_at)}
                  </p>
                </div>
              </div>
              
              <div className="pt-2 border-t border-border/30">
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Stato ordine
                </label>
                <Select
                  value={order.status}
                  onValueChange={(value) => handleStatusChange(order.id, value)}
                  disabled={updatingId === order.id}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminOrders;